'use client'
import { Flex, Icon, Text } from '@core/ui-elements'
import {
  _IAssortmentType,
  _IDeliveryStatus,
  IDeliveryNormalized,
} from '@data/types'
import { getStatusTime, getTimerLimits } from '@utils/datetime'
import { firstAndLastNames, lastNamesAbbreviator } from '@utils/formatters'
import { CardColors } from '@utils/styles'
import { FC, useState } from 'react'
import { MdOutlinePerson } from 'react-icons/md'

import { Card } from '../card'
import { CountdownTimer } from '../countdown-timer'
import { StatCard } from '../stats-cards'
import { SuperDakiTag } from '../super-daki-tag'

export interface IPreparingOrderCard {
  delivery: IDeliveryNormalized
  secondsCountdown: number
  warningThresholdPercent?: number
  criticalThresholdPercent?: number
  handleClick?: () => void
}

export const PreparingOrderCard: FC<IPreparingOrderCard> = ({
  delivery,
  secondsCountdown,
  warningThresholdPercent,
  criticalThresholdPercent,
  handleClick,
}) => {
  const [cardColor, setCardColor] = useState<CardColors>(CardColors.gray)

  const isSuperDaki = delivery.assortmentType === _IAssortmentType.plus
  const orderIdSlices = delivery.orderId.split('-')
  const orderId = orderIdSlices[orderIdSlices.length - 1]

  const { warningThresholdMS, criticalThresholdMS, handleColorChange } =
    getTimerLimits({
      secondsCountdown,
      warningThresholdPercent,
      criticalThresholdPercent,
      defaultColor: CardColors.grayWhite,
    })

  const currentStatusTime = getStatusTime(
    delivery.statusHistory,
    _IDeliveryStatus.picking
  )

  const handleTimeChange = (time: number) => {
    if (!warningThresholdMS || !criticalThresholdMS) return

    const color = handleColorChange(time)

    if (cardColor !== color) {
      setCardColor(color)
    }
  }

  return (
    <Card
      color={isSuperDaki ? CardColors.blue : cardColor}
      secondaryArea={
        <Flex
          flexDirection={'row'}
          gap={'0.5rem'}
          justifyContent={'space-between'}
          width={'100%'}
        >
          <Text width="13ch">{orderId}</Text>

          <Flex
            alignItems="center"
            gap="0.25rem"
            maxWidth="40%"
            minWidth="15ch"
            overflow={'hidden'}
            textOverflow={'ellipsis'}
            textTransform={'capitalize'}
            translate="no"
            whiteSpace={'nowrap'}
            width="23ch"
          >
            <Icon as={MdOutlinePerson} boxSize="1.25rem" />
            {lastNamesAbbreviator(
              firstAndLastNames(delivery.shopper?.fullName)
            ) || '-'}
          </Flex>
        </Flex>
      }
      onClick={handleClick}
    >
      <StatCard
        label={lastNamesAbbreviator(delivery.customer.name)}
        rightSize={
          <Flex flexWrap={'nowrap'} gap={'0.5rem'}>
            <SuperDakiTag showTag={isSuperDaki} />
            <CountdownTimer
              bgColor="white"
              callAtTime={handleTimeChange}
              colorIcon={cardColor}
              countTime={secondsCountdown + 1}
              targetTime={currentStatusTime}
            />
          </Flex>
        }
      />
    </Card>
  )
}
